// Background.tsx — Deep-space backdrop: a near-black clear colour, exponential
// fog to fade distant arms, a faint ambient fill, and a drei <Stars> field that
// rides with the camera (a skybox dome) so dollying never moves the stars across
// the near/far planes — which was causing them to flicker on zoom.
import { useEffect, useRef } from 'react';
import { Stars } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { Color, FogExp2, type Group } from 'three';

export function Background() {
  const scene = useThree((state) => state.scene);
  const dome = useRef<Group>(null);

  useEffect(() => {
    // Deep blue-black void; gentler fog so the far arms + nebulae stay visible.
    scene.background = new Color('#03040a');
    scene.fog = new FogExp2('#04060f', 0.0028);
    return () => {
      scene.background = null;
      scene.fog = null;
    };
  }, [scene]);

  // Keep the star dome centred on the camera so the stars read as an
  // infinitely-distant backdrop and never clip/pop as the camera dollies.
  useFrame((state) => {
    dome.current?.position.copy(state.camera.position);
  });

  return (
    <>
      {/* Low, cool fill (faint starlight) — kept dim so planet night sides go
          genuinely dark and the day/night terminator reads as a real shadow rather
          than a washed-out, evenly-lit disc. */}
      <ambientLight intensity={0.18} color="#6678b0" />
      <group ref={dome}>
        {/* Two layered fields: a dense faint scatter + brighter coloured stars. */}
        <Stars radius={320} depth={140} count={9000} factor={4} saturation={0} fade speed={0.3} />
        <Stars radius={260} depth={90} count={1600} factor={7} saturation={0.6} fade speed={0.2} />
      </group>
    </>
  );
}
